import { Fragment, useDeferredValue, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, StatTile } from '../../components/ui/Card';
import { Input, Select } from '../../components/ui/Input';
import { Nil, Table, TableFooter, THead, Td, Th, Tr } from '../../components/ui/Table';
import { InfoTip } from '../../components/ui/InfoTip';
import { EmptyState, ErrorState, LoadingState, errorMessage } from '../../components/ui/States';
import { formatDayMonth, formatDayMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { cleanText, daysAgo } from './format';
import { channelLabel, displayCompanyName } from './labels';
import { AttioCompanyLink } from '../Dashboard/csm/attio';
import { useFunnelAccounts, useFunnelOverview, useStageHistory, useStageOverride } from '../../hooks/useSignalOps';
import {
  BAND_LABELS,
  FUNNEL_LADDER,
  FUNNEL_SECTION_LABELS,
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGE_LONG,
  FUNNEL_STATES,
  ICP_FIT_LABELS,
  type FunnelAccountsParams,
  type FunnelOverview,
  type FunnelStage,
  type FunnelStageRow,
  type IcpFit,
  type ScoreBand,
} from '../../api/signalOps';

/**
 * Funnel — where every company is, next to how warm it is, and who has to act.
 *
 * Stage and heat are two axes on purpose: the heat band (Accounts tab) decays
 * in weeks, the stage holds. Stage names are Lea's lifecycle moved to the
 * account. Nothing here writes to Attio.
 */

const HOW_IT_WORKS = (
  <>
    <p>
      <strong>Stages are evaluated top-down every night</strong>, after scoring. The highest stage whose rule holds wins.
      Heat (hot → cold) is the same score as on Accounts and the Points tab.
    </p>
    <ul className="mt-2 list-disc space-y-1 pl-4">
      <li><strong>Customer</strong>: paying in Odoo, or a deal won in the last year.</li>
      <li><strong>Opportunity</strong>: an open Attio deal past prospecting.</li>
      <li><strong>SAL</strong> (Sales Accepted Lead): a prospecting deal, a demo or trial request, a booked demo, or an inbound reply to a rep. A hand-raise skips MQA.</li>
      <li><strong>MQA</strong> (Marketing Qualified Account): a core or adjacent ICP company whose score reaches the MQA threshold on the Points tab, with at least one signal beyond a light touch. It stays MQA until the score falls below half the threshold.</li>
      <li><strong>MAL</strong> (Marketing Accepted Lead): any real engagement in 90 days. Opens and clicks on our own outbound never count.</li>
      <li><strong>Target</strong>: core or adjacent ICP, no engagement yet.</li>
    </ul>
    <p className="mt-2">
      A lost deal parks the company as Recycled for 90 days. MAL and SAL hold while the company keeps engaging meaningfully (up to 150 days) — only about 1 in 6 visitors is tracked, so silence is not proof of cold — then step down one stage a month.
    </p>
  </>
);

const BAND_DOT: Record<string, string> = {
  hot: 'bg-error',
  warm: 'bg-warning',
  cool: 'bg-text-tertiary',
  cold: 'bg-border-dark',
};

const TREND_MARK: Record<string, string> = { warming: '↑', cooling: '↓' };

function stageLabel(stage: string | null | undefined): string {
  if (!stage) return '—';
  return FUNNEL_STAGE_LABELS[stage as FunnelStage] ?? stage;
}

function BandMark({ band, trend }: { band: string | null; trend?: string | null }) {
  if (!band) return <span className="text-body-sm text-text-tertiary">Unscored</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-body-sm text-text-secondary">
      <span aria-hidden className={clsx('h-2 w-2 rounded-full', BAND_DOT[band] ?? 'bg-border-dark')} />
      {BAND_LABELS[band as ScoreBand] ?? band}
      {trend && TREND_MARK[trend] && <span className="text-text-tertiary" title={trend}>{TREND_MARK[trend]}</span>}
    </span>
  );
}

function People({ row }: { row: FunnelStageRow }) {
  if (row.people.length === 0) return <span className="text-text-tertiary">No known person yet</span>;
  return (
    <span className="text-text-secondary">
      {row.people.map((p, i) => (
        <span key={i}>
          {i > 0 && ', '}
          {p.name}
          {p.title && <span className="text-text-tertiary"> ({p.title})</span>}
        </span>
      ))}
    </span>
  );
}

function pct(n: number, d: number): string {
  if (d === 0) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

// ── Overview ──────────────────────────────────────────────────────────────────

function Overview({ data, onPick }: { data: FunnelOverview; onPick: (p: { stage: string; band?: string }) => void }) {
  const grid = new Map(data.grid.map((g) => [g.stage, g]));
  const entries = new Map(data.entries.map((e) => [e.stage, e]));
  const count = (s: string) => grid.get(s)?.total ?? 0;
  const new7 = (entries.get('mqa')?.last7 ?? 0) + (entries.get('sal')?.last7 ?? 0);
  const outside = FUNNEL_STATES.filter((s) => count(s) > 0);

  const driversByStage = new Map<string, Array<{ channel: string; n: number }>>();
  for (const d of data.drivers) {
    const list = driversByStage.get(d.stage) ?? [];
    list.push({ channel: d.channel, n: d.n });
    driversByStage.set(d.stage, list);
  }

  const gridRow = (s: string) => {
    const g = grid.get(s);
    const e = entries.get(s);
    const cell = (band: string, n: number | undefined) =>
      n ? (
        <button
          type="button"
          className="tabular-nums text-text-primary hover:underline"
          onClick={(ev) => {
            ev.stopPropagation();
            onPick({ stage: s, band });
          }}
        >
          {formatNumber(n)}
        </button>
      ) : (
        <Nil />
      );
    return (
      <Tr key={s} onClick={() => onPick({ stage: s })}>
        <Td className="font-medium text-text-primary" title={FUNNEL_STAGE_LONG[s as FunnelStage]}>{stageLabel(s)}</Td>
        <Td className="text-text-tertiary">{FUNNEL_SECTION_LABELS[s as FunnelStage] ?? 'Outside the funnel'}</Td>
        <Td align="right">{formatNumber(g?.total ?? 0)}</Td>
        <Td align="right">{cell('hot', g?.hot)}</Td>
        <Td align="right">{cell('warm', g?.warm)}</Td>
        <Td align="right">{cell('cool', g?.cool)}</Td>
        <Td align="right">{cell('cold', g?.cold)}</Td>
        <Td align="right">{cell('unscored', g?.unscored)}</Td>
        <Td align="right">{e?.last7 || <Nil />}</Td>
        <Td align="right">{e?.last30 || <Nil />}</Td>
      </Tr>
    );
  };

  return (
    <div className="space-y-4">
      {data.lastRun && data.lastRun.warnings.length > 0 && (
        <div role="alert" className="rounded border border-warning/40 bg-warning-light px-4 py-3 text-body-sm text-warning-dark">
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle aria-hidden className="h-4 w-4" /> The last stage pass ({formatDayMonthYear(data.lastRun.ranAt)}) flagged:
          </p>
          <ul className="mt-1 list-disc pl-6">
            {data.lastRun.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="TOFU · MAL" value={formatNumber(count('mal'))} sub="Engaged in 90 days" onClick={() => onPick({ stage: 'mal' })} />
        <StatTile label="MOFU · MQA" value={formatNumber(count('mqa'))} sub={`ICP, score ${data.rules.mqaThreshold}+`} onClick={() => onPick({ stage: 'mqa' })} />
        <StatTile
          label="BOFU · SAL + Opp."
          value={formatNumber(count('sal') + count('opportunity'))}
          sub={`${formatNumber(count('sal'))} SAL · ${formatNumber(count('opportunity'))} opportunities`}
          onClick={() => onPick({ stage: 'sal' })}
        />
        <StatTile label="Into MQA or SAL" value={formatNumber(new7)} sub="Last 7 days, live moves only" />
      </div>

      <NeedsAction data={data} />

      <Card>
        <CardHeader
          title="Stage × heat"
          description={`Where each company is, split by today's heat band. Click a number to list those companies · evaluated ${formatDayMonthYear(data.evaluatedAt!)}`}
        />
        <Table minWidth={720}>
          <THead>
            <tr>
              <Th>Stage</Th>
              <Th>Funnel</Th>
              <Th align="right">Companies</Th>
              <Th align="right">Hot</Th>
              <Th align="right">Warm</Th>
              <Th align="right">Cool</Th>
              <Th align="right">Cold</Th>
              <Th align="right">Unscored</Th>
              <Th align="right" title="Live moves into the stage; rebuilt history excluded">New 7d</Th>
              <Th align="right" title="Live moves into the stage; rebuilt history excluded">New 30d</Th>
            </tr>
          </THead>
          <tbody>
            {FUNNEL_LADDER.map(gridRow)}
            {outside.length > 0 && (
              <tr>
                <td colSpan={10} className="bg-surface-secondary py-1.5 text-eyebrow uppercase text-text-tertiary">Outside the funnel</td>
              </tr>
            )}
            {outside.map(gridRow)}
          </tbody>
        </Table>
        <TableFooter>
          Only about 1 in 6 website visitors is tracked, so MAL and Target undercount real interest.
          {data.unclassifiedEngaged > 0 &&
            ` ${formatNumber(data.unclassifiedEngaged)} MAL companies have no ICP classification and cannot become MQA until they are classified in Attio.`}
        </TableFooter>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Moving up"
            description={
              <>
                Companies that entered a stage in the last {data.periodDays} days. The share counts only those that entered at least {data.matureDays} days ago, so new arrivals don’t drag it down.
                {data.includesRebuiltHistory && ' Part of this history was rebuilt week by week, so day counts move in steps of 7.'} Small numbers — read the direction, not the decimals.
              </>
            }
          />
          <Table>
            <THead>
              <tr>
                <Th>Entered</Th>
                <Th align="right">Companies</Th>
                <Th align="right" title={`Entered at least ${data.matureDays} days ago`}>Old enough</Th>
                <Th align="right">Later moved up</Th>
                <Th align="right">Share</Th>
                <Th align="right">Median days</Th>
              </tr>
            </THead>
            <tbody>
              {data.conversion.map((r) => (
                <Tr key={r.fromStage}>
                  <Td className="font-medium text-text-primary">{stageLabel(r.fromStage)}</Td>
                  <Td align="right">{formatNumber(r.entered)}</Td>
                  <Td align="right">{formatNumber(r.matured)}</Td>
                  <Td align="right">{formatNumber(r.advanced)}</Td>
                  <Td
                    align="right"
                    className={clsx(r.matured < 10 && 'text-text-tertiary')}
                    title={r.matured < 10 ? 'Fewer than 10 companies — too few to read' : undefined}
                  >
                    {pct(r.advanced, r.matured)}
                  </Td>
                  <Td align="right">{r.medianDaysToAdvance ?? <Nil />}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <Card>
          <CardHeader
            title="What moved them"
            description={`Every channel that contributed to an entry, last ${data.periodDays} days — one entry can credit several. Deal-driven moves carry no channel.`}
          />
          {(['sal', 'mqa', 'mal'] as const).every((s) => !driversByStage.get(s)?.length) ? (
            <p className="text-body-sm text-text-tertiary">No signal-driven entries yet.</p>
          ) : (
            <div className="space-y-3">
              {(['sal', 'mqa', 'mal'] as const).map((s) => {
                const list = driversByStage.get(s) ?? [];
                if (list.length === 0) return null;
                return (
                  <div key={s}>
                    <p className="eyebrow mb-1">Into {stageLabel(s)}</p>
                    <ul className="space-y-0.5 text-body-sm">
                      {list.slice(0, 6).map((d) => (
                        <li key={d.channel} className="flex justify-between gap-4">
                          <span className="text-text-secondary">{channelLabel(d.channel)}</span>
                          <span className="tabular-nums text-text-primary">{formatNumber(d.n)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {data.assets.length > 0 && (
                <div>
                  <p className="eyebrow mb-1">Campaigns, forms and pages behind MQA and SAL entries</p>
                  <ul className="space-y-0.5 text-body-sm">
                    {data.assets.slice(0, 8).map((a) => (
                      <li key={a.asset} className="flex justify-between gap-4">
                        <span className="truncate text-text-secondary" title={a.asset}>{cleanText(a.asset)}</span>
                        <span className="tabular-nums text-text-primary">{formatNumber(a.n)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function NeedsAction({ data }: { data: FunnelOverview }) {
  const [open, setOpen] = useState<string | null>(null);
  const { rows, total } = data.needsAction;
  return (
    <Card padding="none">
      <div className="px-4 pt-4 pb-3">
        <h3 className="text-body-md font-semibold text-text-primary">Needs action</h3>
        <p className="text-body-xs text-text-tertiary">
          SAL older than {data.rules.salSlaDays} days or MQA older than {data.rules.mqaSlaDays} days that rest on signals alone — no deal, and nobody has accepted or recycled them yet. Most overdue first.
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="px-4 pb-4 text-body-sm text-text-tertiary">Nothing overdue.</p>
      ) : (
        <>
          <CompanyTable rows={rows} expanded={open} setExpanded={setOpen} showOverdue />
          {total > rows.length && <TableFooter>{`Most overdue ${rows.length} of ${formatNumber(total)}`}</TableFooter>}
        </>
      )}
    </Card>
  );
}

// ── One company: who, history, and the decision ──────────────────────────────

const REASON_CHIPS = ['Spoke to them — real project', 'No project this year', 'Wrong persona', 'Competitor locked in', 'Not a fit (size or sector)'];

function CompanyDetail({ row }: { row: FunnelStageRow }) {
  const history = useStageHistory(row.companyId);
  const override = useStageOverride();
  const [reason, setReason] = useState('');
  const isSal = row.stage === 'sal';

  const actions: Array<{ stage: FunnelStage; label: string; hint: string; variant: 'secondary' | 'danger'; hidden: boolean }> = [
    {
      stage: 'sal',
      label: isSal ? 'Confirm as SAL' : 'Accept as SAL',
      hint: 'Sales takes it on. Lapses after 90 days.',
      variant: 'secondary',
      hidden: row.overrideStage === 'sal',
    },
    { stage: 'recycled', label: 'Not now — recycle', hint: 'Back to nurture for 90 days, then the rules decide again.', variant: 'secondary', hidden: row.overrideStage === 'recycled' },
    { stage: 'disqualified', label: 'Disqualify', hint: 'Out for a year, then the rules decide again.', variant: 'danger', hidden: row.overrideStage === 'disqualified' },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 bg-surface-secondary px-4 py-4 lg:grid-cols-2">
      <div>
        <p className="eyebrow mb-2">Who</p>
        <p className="mb-4 text-body-sm"><People row={row} /></p>
        <p className="eyebrow mb-2">Stage history</p>
        {history.isLoading ? (
          <LoadingState rows={2} />
        ) : history.isError ? (
          <ErrorState compact error={history.error} onRetry={() => void history.refetch()} />
        ) : (
          <ol className="space-y-2 text-body-sm">
            {(history.data?.rows ?? []).map((h, i) => (
              <li key={i}>
                <span className="text-text-tertiary">{formatDayMonthYear(h.occurredAt)}</span>{' '}
                <span className="font-medium text-text-primary">
                  {h.kind === 'confirmed'
                    ? `${stageLabel(h.toStage)} confirmed`
                    : `${h.fromStage ? `${stageLabel(h.fromStage)} → ` : ''}${stageLabel(h.toStage)}`}
                </span>
                {h.manual && <span className="ml-1.5"><Badge variant="info">by hand{h.decidedBy ? ` · ${h.decidedBy}` : ''}</Badge></span>}
                {h.backfilled && <span className="ml-1.5 text-body-xs text-text-tertiary">(rebuilt)</span>}
                {h.alertState === 'pending' && <span className="ml-1.5"><Badge variant="warning">alert not sent yet</Badge></span>}
                <p className="text-text-secondary">
                  {cleanText(h.reason)}
                  {h.channels && h.channels.length > 0 ? ` · ${h.channels.map(channelLabel).join(', ')}` : ''}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div>
        <p className="eyebrow mb-2">Decide</p>
        <p className="mb-3 text-body-sm text-text-secondary">
          The rules say <strong>{stageLabel(row.computedStage)}</strong>.
          {row.overrideStage && row.overrideExpiresAt
            ? ` ${row.overrideStage === 'sal' ? 'Accepted' : `Set to ${stageLabel(row.overrideStage)}`} by ${row.overrideBy ?? 'someone'} (“${row.overrideReason ?? ''}”) until ${formatDayMonthYear(row.overrideExpiresAt)}.`
            : ''}
        </p>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {REASON_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setReason(c)}
              className={clsx(
                'rounded-full border px-2.5 py-0.5 text-body-xs transition-colors',
                reason === c ? 'border-text-primary text-text-primary' : 'border-border-light text-text-secondary hover:border-border',
              )}
            >
              {c}
            </button>
          ))}
        </div>
        <Input
          id={`stage-reason-${row.companyId}`}
          aria-label="Reason"
          placeholder="Reason — pick one above or write your own (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="mt-2 flex flex-wrap gap-2">
          {actions.filter((a) => !a.hidden).map((a) => (
            <Button
              key={a.stage}
              size="sm"
              variant={a.variant}
              title={a.hint}
              disabled={reason.trim().length < 3 || override.isPending}
              onClick={() => override.mutate({ companyId: row.companyId, stage: a.stage, reason: reason.trim() }, { onSuccess: () => setReason('') })}
            >
              {a.label}
            </Button>
          ))}
          {row.overrideStage && (
            <Button size="sm" variant="ghost" disabled={override.isPending} onClick={() => override.mutate({ companyId: row.companyId, clear: true })}>
              Back to the rules
            </Button>
          )}
        </div>
        {reason.trim().length < 3 && <p className="mt-1.5 text-body-xs text-text-tertiary">Every decision needs a reason.</p>}
        {override.isError && <p className="mt-2 text-body-sm text-error-dark">Not saved: {errorMessage(override.error)}</p>}
      </div>
    </div>
  );
}

// ── Companies ─────────────────────────────────────────────────────────────────

function CompanyTable({
  rows,
  expanded,
  setExpanded,
  showOverdue = false,
}: {
  rows: FunnelStageRow[];
  expanded: string | null;
  setExpanded: (id: string | null) => void;
  showOverdue?: boolean;
}) {
  return (
    <Table minWidth={900} colWidths={['1.5rem', '13rem', '7rem', '6.5rem', '7.5rem', '7.5rem', 'auto']}>
      <THead>
        <tr>
          <Th className="pl-4" />
          <Th>Company</Th>
          <Th>Stage</Th>
          <Th>Heat</Th>
          <Th>ICP fit</Th>
          <Th>In stage</Th>
          <Th>Why · who</Th>
        </tr>
      </THead>
      <tbody>
        {rows.map((r) => {
          const open = expanded === r.companyId;
          const overdue = (r.overdueDays ?? 0) > 0;
          return (
            <Fragment key={r.companyId}>
              <Tr onClick={() => setExpanded(open ? null : r.companyId)}>
                <Td className="pl-4">
                  <ChevronRight aria-hidden className={clsx('h-3.5 w-3.5 text-text-tertiary transition-transform', open && 'rotate-90')} />
                </Td>
                <Td className="font-medium text-text-primary">
                  <span className="inline-flex max-w-full items-center gap-1">
                    <span className="truncate" title={r.domain ?? undefined}>{displayCompanyName(cleanText(r.name))}</span>
                    {r.attioRecordId && <AttioCompanyLink recordId={r.attioRecordId} name={r.name} />}
                  </span>
                </Td>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1.5" title={FUNNEL_STAGE_LONG[r.stage as FunnelStage]}>
                    {stageLabel(r.stage)}
                    {r.overrideStage && <Badge variant="info">{r.overrideStage === 'sal' ? 'accepted' : 'by hand'}</Badge>}
                  </span>
                </Td>
                <Td><BandMark band={r.band} trend={r.trend} /></Td>
                <Td className="text-text-secondary">{r.icpFit ? ICP_FIT_LABELS[r.icpFit as IcpFit] ?? r.icpFit : 'Unclassified'}</Td>
                <Td
                  className={clsx(overdue ? 'font-medium text-error-dark' : 'text-text-secondary')}
                  title={`Since ${formatDayMonthYear(r.stageEnteredAt)}${overdue ? ` · ${r.overdueDays} days past target` : ''}`}
                >
                  {showOverdue && overdue ? `${r.overdueDays}d overdue` : `${formatDayMonth(r.stageEnteredAt)} · ${daysAgo(r.stageEnteredAt)}`}
                </Td>
                <Td className="truncate text-text-secondary" title={cleanText(r.reason)}>
                  {cleanText(r.reason)}
                  {r.people.length > 0 && <span className="text-text-tertiary"> · {r.people.map((p) => p.name).filter(Boolean).join(', ')}</span>}
                </Td>
              </Tr>
              {open && (
                <tr>
                  <td colSpan={7}>
                    <CompanyDetail row={r} />
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </Table>
  );
}

const PAGE = 100;

function Companies({
  filters,
  setFilters,
  focusId,
  clearFocus,
}: {
  filters: { stage: string; band: string };
  setFilters: (f: { stage: string; band: string }) => void;
  focusId: string | null;
  clearFocus: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(focusId);
  const [q, setQ] = useState('');
  const [icpFit, setIcpFit] = useState('');
  const [sort, setSort] = useState<'recent' | 'heat' | 'oldest'>('heat');
  const [limit, setLimit] = useState(PAGE);
  const query = useDeferredValue(q);

  const params: FunnelAccountsParams = { limit, sort };
  if (focusId) params.companyId = focusId;
  else {
    if (filters.stage) params.stage = filters.stage;
    if (filters.band) params.band = filters.band;
    if (icpFit) params.icpFit = icpFit;
    if (query.trim()) params.q = query.trim();
  }
  const { data, isLoading, isError, error, refetch } = useFunnelAccounts(params);
  const rows = data?.rows ?? [];

  return (
    <Card padding="none">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4 pb-3">
        <h3 className="text-body-md font-semibold text-text-primary">Companies</h3>
        {focusId ? (
          <>
            <p className="text-body-sm text-text-tertiary">One company, from an alert link</p>
            <Button size="sm" variant="ghost" onClick={clearFocus}>Show all</Button>
          </>
        ) : (
          <div className="ml-auto flex flex-wrap gap-2">
            <div className="w-48">
              <Input id="funnel-search" aria-label="Search companies" placeholder="Search name or domain" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select size="sm" id="funnel-stage-filter" aria-label="Stage" value={filters.stage} onChange={(e) => setFilters({ ...filters, stage: e.target.value })}>
              <option value="">All stages</option>
              {[...FUNNEL_LADDER, ...FUNNEL_STATES].map((s) => (
                <option key={s} value={s}>{FUNNEL_STAGE_LABELS[s]}</option>
              ))}
            </Select>
            <Select size="sm" id="funnel-band-filter" aria-label="Heat" value={filters.band} onChange={(e) => setFilters({ ...filters, band: e.target.value })}>
              <option value="">Any heat</option>
              {(['hot', 'warm', 'cool', 'cold'] as ScoreBand[]).map((b) => <option key={b} value={b}>{BAND_LABELS[b]}</option>)}
              <option value="unscored">Unscored</option>
            </Select>
            <Select size="sm" id="funnel-fit-filter" aria-label="ICP fit" value={icpFit} onChange={(e) => setIcpFit(e.target.value)}>
              <option value="">Any ICP fit</option>
              {(['core', 'adjacent', 'out'] as IcpFit[]).map((f) => <option key={f} value={f}>{ICP_FIT_LABELS[f]}</option>)}
              <option value="unclassified">Unclassified</option>
            </Select>
            <Select size="sm" id="funnel-sort" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
              <option value="heat">Hottest first</option>
              <option value="recent">Newest in stage</option>
              <option value="oldest">Longest in stage</option>
            </Select>
          </div>
        )}
      </div>
      {isLoading ? (
        <div className="px-4 pb-4"><LoadingState rows={5} /></div>
      ) : isError ? (
        <div className="px-4 pb-4"><ErrorState error={error} onRetry={() => void refetch()} /></div>
      ) : rows.length === 0 ? (
        <div className="px-4 pb-4"><EmptyState title="No companies match" description="Try another stage, heat or fit — or clear the search." /></div>
      ) : (
        <>
          <CompanyTable rows={rows} expanded={expanded} setExpanded={setExpanded} />
          <TableFooter
            action={
              data && data.total > rows.length ? (
                <Button size="sm" variant="ghost" onClick={() => setLimit((l) => Math.min(l + PAGE, 1000))}>
                  Show {Math.min(PAGE, data.total - rows.length)} more
                </Button>
              ) : undefined
            }
          >
            {data && data.total > rows.length
              ? `${formatNumber(rows.length)} of ${formatNumber(data.total)}`
              : `${formatNumber(rows.length)} ${rows.length === 1 ? 'company' : 'companies'}`}
          </TableFooter>
        </>
      )}
    </Card>
  );
}

export function FunnelView() {
  const [search, setSearch] = useSearchParams();
  const focusId = search.get('company');
  const [filters, setFiltersState] = useState({ stage: 'mqa', band: '' });
  const overview = useFunnelOverview(180);

  const clearFocus = () => {
    if (!focusId) return;
    const next = new URLSearchParams(search);
    next.delete('company');
    setSearch(next, { replace: true });
  };
  const setFilters = (f: { stage: string; band: string }) => {
    setFiltersState(f);
    clearFocus();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-body-sm text-text-tertiary">Where each company is in the funnel, how warm it is, and who has to act.</p>
        <InfoTip>{HOW_IT_WORKS}</InfoTip>
      </div>
      {overview.isLoading ? (
        <LoadingState rows={4} />
      ) : overview.isError || !overview.data ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : !overview.data.evaluatedAt ? (
        <EmptyState title="No stages yet" description="Stages are written by the nightly Signal Ops run. An admin can rebuild history with the backfill." />
      ) : (
        <Overview data={overview.data} onPick={({ stage, band }) => setFilters({ stage, band: band ?? '' })} />
      )}
      <Companies key={focusId ?? `${filters.stage}|${filters.band}`} filters={filters} setFilters={setFilters} focusId={focusId} clearFocus={clearFocus} />
    </div>
  );
}
